<div class="tdb-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    [title]="'testdatenblaetter.pageTitle' | translate"
    [subtitle]="'testdatenblaetter.pageSubtitle' | translate"
  >
    <ng-container right>
      <app-stat-group
        class="tdb-header-stats"
        role="group"
        [items]="statItems()"
        [attr.aria-label]="statsAriaLabel()"
      />
      @if (!isProjectReader()) {
        <button mat-flat-button color="primary" type="button" class="tdb-create-btn" (click)="onCreate()">
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'testdatenblaetter.addButton' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="tdb-toolbar">
    <app-search-bar
      class="tdb-search"
      data-testid="tdb-search"
      [placeholder]="'testdatenblaetter.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <app-filter-dropdown
      name="author"
      [value]="selectedAuthor()"
      [placeholder]="'testdatenblaetter.filter.author' | translate"
      [options]="authorOptions()"
      (valueChange)="onAuthorChange($event)"
    />
    <app-saved-views-menu
      data-testid="tdb-saved-views"
      [views]="savedViewsOptions()"
      [activeName]="activeSavedViewName()"
      [context]="'TEST_DATA_SHEETS'"
      [projectId]="projectId"
      (viewSelected)="onSavedViewSelected($event)"
      (viewSaved)="onSavedViewSaved($event)"
      (viewDeleted)="onSavedViewDeleted($event)"
      (viewDeleteFailed)="onSavedViewDeleteFailed()"
    />
    @if (hasFilters()) {
      <app-reset-filter-button [label]="'testdatenblaetter.filter.reset' | translate" (resetClick)="onResetFilters()" />
    }
    <span class="tdb-result-count" data-testid="tdb-counter">
      {{ resultCountKey() | translate: { count: total(), total: unfilteredTotal() } }}
    </span>
  </div>

  @if (savedViewsError()) {
    <p class="tdb-hint tdb-hint--error" role="alert">{{ savedViewsError() }}</p>
  }

  @if (deleteError()) {
    <p class="tdb-hint tdb-hint--error" role="alert">
      {{ 'testdatenblaetter.deleteError' | translate }}
    </p>
  }

  @if (loading()) {
    <div class="tdb-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="tdb-hint tdb-hint--error" role="alert">{{ 'testdatenblaetter.loadError' | translate }}</p>
  } @else if (rows().length === 0) {
    <p class="tdb-hint">{{ 'testdatenblaetter.empty' | translate }}</p>
  } @else if (!hasResults()) {
    <p class="tdb-hint tdb-hint--filtered">{{ 'testdatenblaetter.emptyFiltered' | translate }}</p>
  } @else {
    <div
      appListFocusScroll
      class="tdb-card"
      role="table"
      [attr.aria-label]="'testdatenblaetter.tableLabel' | translate"
    >
      <div class="tdb-table-header" role="row">
        <div class="tdb-col tdb-col--id" role="columnheader">{{ 'testdatenblaetter.table.id' | translate }}</div>
        <div class="tdb-col tdb-col--name" role="columnheader">{{ 'testdatenblaetter.table.name' | translate }}</div>
        <div class="tdb-col tdb-col--vars" role="columnheader">
          {{ 'testdatenblaetter.table.variables' | translate }}
        </div>
        <div class="tdb-col tdb-col--rows" role="columnheader">{{ 'testdatenblaetter.table.rows' | translate }}</div>
        <div class="tdb-col tdb-col--modules" role="columnheader">
          {{ 'testdatenblaetter.table.modules' | translate }}
        </div>
        <div class="tdb-col tdb-col--author" role="columnheader">
          {{ 'testdatenblaetter.table.author' | translate }}
        </div>
        <div class="tdb-col tdb-col--actions" role="columnheader">
          {{ 'testdatenblaetter.table.actions' | translate }}
        </div>
      </div>

      @for (row of pagedRows(); track row.id; let last = $last) {
        <div
          class="tdb-row"
          [class.tdb-row--last]="last"
          role="row"
          tabindex="0"
          [attr.aria-label]="row.name"
          (click)="onEdit(row)"
          (keydown.enter)="onRowKeydown($event, row)"
        >
          <div class="tdb-col tdb-col--id" role="cell">
            <app-code-id-chip [id]="row.displayId" />
          </div>
          <div class="tdb-col tdb-col--name" role="cell">
            <span class="tdb-name">{{ row.name }}</span>
            @if (row.description) {
              <span class="tdb-desc">{{ row.description }}</span>
            }
          </div>
          <div class="tdb-col tdb-col--vars" role="cell">
            <span class="tdb-num">{{ row.variableCount }}</span>
          </div>
          <div class="tdb-col tdb-col--rows" role="cell">
            <span class="tdb-pill">{{ row.rowCount }}</span>
          </div>
          <div class="tdb-col tdb-col--modules" role="cell">
            @if (row.moduleCount > 0) {
              <span class="tdb-modules">
                {{ 'testdatenblaetter.moduleLinked' | translate: { count: row.moduleCount } }}
              </span>
            } @else {
              <span class="tdb-none" [attr.aria-label]="'testdatenblaetter.moduleNone' | translate">—</span>
            }
          </div>
          <div class="tdb-col tdb-col--author" role="cell">
            @if (row.authorName) {
              <app-avatar [name]="row.authorName" [size]="28" aria-hidden="true" />
              <span class="tdb-author__name">{{ row.authorName }}</span>
            } @else {
              <span class="tdb-none" [attr.aria-label]="'testdatenblaetter.table.noAuthor' | translate">—</span>
            }
          </div>
          <div class="tdb-col tdb-col--actions" role="cell">
            @if (!isProjectReader()) {
              <button
                type="button"
                class="tdb-action-btn tdb-action-btn--accent"
                [attr.aria-label]="'testdatenblaetter.edit' | translate"
                (click)="$event.stopPropagation(); onEdit(row)"
              >
                <mat-icon aria-hidden="true">edit</mat-icon>
              </button>
              <button
                type="button"
                class="tdb-action-btn tdb-action-btn--danger"
                [attr.aria-label]="'testdatenblaetter.delete' | translate"
                (click)="$event.stopPropagation(); onDeleteRequest(row)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>

    <div class="tdb-footer">
      <span class="tdb-footer__range" role="status">
        {{
          'testdatenblaetter.footer.rangeLabel' | translate: { start: rangeStart(), end: rangeEnd(), total: total() }
        }}
      </span>
      <div class="tdb-footer__pager">
        <label class="tdb-footer__label" for="tdb-page-size">
          {{ 'testdatenblaetter.footer.rowsPerPage' | translate }}
        </label>
        <select
          id="tdb-page-size"
          class="tdb-footer__select"
          (change)="onPageSizeChange(+pageSizeSelect.value)"
          #pageSizeSelect
          [attr.aria-label]="'testdatenblaetter.footer.rowsPerPageAriaLabel' | translate"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
          }
        </select>
        @if (pageCount() > 1) {
          <div class="tdb-footer__nav">
            <button
              type="button"
              class="tdb-action-btn"
              [disabled]="pageIndex() === 0"
              [attr.aria-label]="'testdatenblaetter.footer.previousPage' | translate"
              (click)="onPageChange(pageIndex() - 1)"
            >
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
            </button>
            <button
              type="button"
              class="tdb-action-btn"
              [disabled]="pageIndex() >= pageCount() - 1"
              [attr.aria-label]="'testdatenblaetter.footer.nextPage' | translate"
              (click)="onPageChange(pageIndex() + 1)"
            >
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </div>
        }
      </div>
    </div>
  }
</div>

<app-confirm-dialog
  [visible]="showDeleteModal()"
  [title]="'testdatenblaetter.deleteTitle' | translate"
  [message]="deleteMessage()"
  [confirmLabel]="'testdatenblaetter.deleteConfirm' | translate"
  [cancelLabel]="'testdatenblaetter.deleteCancel' | translate"
  [busy]="deleting()"
  (confirmed)="onConfirmDelete()"
  (cancelled)="onCancelDelete()"
/>
